'use client';

import { QueryClientProvider } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { ApiProvider } from '@/contexts/ApiContext';
import { createQueryClient } from '@/lib/queryClient';
import {
  RequestLanguageContext,
  detectAndApplyBrowserLanguage,
  getLanguageFromCookie,
  useLanguageStore,
} from '@/stores/languageStore';
import { useAuthStoreHydrate } from '@/stores/authStore';
import { WebUrlNavigationProvider } from '@/components/WebUrlNavigationProvider';
import { ConfirmDialogHost } from '@/components/ConfirmDialog';
import { useYandexMetrika } from '@/hooks/useYandexMetrika';
import { useFacebookPixel } from '@/hooks/useFacebookPixel';
import { isWikiPath } from '@/lib/wikiPath';
import { isNewsPath } from '@/lib/newsPath';

// WHY: вики и новости — статьи, а не игра: звуки интерфейса тянем, только когда посетитель ушёл в игровые разделы; заставку и музыку Rush Mid
// грузит экран игры в момент входа; модули грузятся тем же импортом по требованию, чтобы их не было в общем бандле страниц вики
function GameAssetsBootstrap() {
  const pathname = usePathname();
  // WHY: на вебе адрес `/` без языка — только страница выбора языка, игровые ресурсы ей не нужны
  const lightPage = isWikiPath(pathname) || isNewsPath(pathname) || pathname === '/';

  useEffect(() => {
    if (lightPage) return;
    void Promise.all([import('@/lib/audioSession'), import('@/lib/uiSounds')]).then(
      ([audio, sounds]) => {
        audio.initAudioSession();
        sounds.preloadUiSounds();
        sounds.initClickSound();
      },
    );
  }, [lightPage]);

  return null;
}

export function Providers({ children }: { children: ReactNode }) {
  const queryClient = useMemo(() => createQueryClient(), []);
  const requestLanguage = useContext(RequestLanguageContext);
  useAuthStoreHydrate();
  useYandexMetrika();
  useFacebookPixel();

  useEffect(() => {
    // WHY: на вебе язык задаёт адрес, а кэшированный HTML вики и публичных экранов приходит без Set-Cookie — куку языка
    // ставим из адреса, когда она с ним расходится
    if (requestLanguage) {
      // WHY: страница выбора на корне язык только советует — запомни его кука, следующий заход на корень увёл бы
      // редиректом по языку браузера
      if (window.location.pathname === '/') return;
      if (getLanguageFromCookie() === requestLanguage) return;
      useLanguageStore.getState().setLanguage(requestLanguage);
    } else {
      detectAndApplyBrowserLanguage();
    }
  }, [requestLanguage]);

  return (
    <QueryClientProvider client={queryClient}>
      <ApiProvider>

        <WebUrlNavigationProvider>
          <GameAssetsBootstrap />
          {children}

          <ConfirmDialogHost />
        </WebUrlNavigationProvider>
      </ApiProvider>
    </QueryClientProvider>
  );
}
